import { useState } from 'react';
import { Outlet, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AscendHorizonLogo, Icon } from '@ascend/shared/components';
import { useAuth } from '../contexts/AuthContext';

function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

// ─── Sidebar Navigation Config ───────────────────────────────────────────────

interface NavItem {
  label: string;
  iconName: string;
  path: string;
}

interface NavSection {
  heading: string | null;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    heading: null,
    items: [
      { label: 'Dashboard', iconName: 'streamline:interface-dashboard-layout-3-app-application-dashboard-home-layout', path: '/dashboard' },
    ],
  },
  {
    heading: 'CREDIT FACILITIES',
    items: [
      { label: 'Credit Facilities', iconName: 'streamline:building-2', path: '/credit-facilities' },
      { label: 'Margin Accounts', iconName: 'streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user', path: '/margin-accounts' },
    ],
  },
  {
    heading: 'LIQUIDITY',
    items: [
      { label: 'Stablecoin Pools', iconName: 'streamline:bank', path: '/stablecoin-pools' },
    ],
  },
  {
    heading: 'RISK & LIQUIDATIONS',
    items: [
      { label: 'Liquidation Monitor', iconName: 'streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court', path: '/liquidation' },
      { label: 'Compliance Controls', iconName: 'streamline:shield-check', path: '/compliance' },
    ],
  },
  {
    heading: 'IDENTITY & CLAIMS',
    items: [
      { label: 'Claims', iconName: 'streamline:clipboard-check', path: '/claims' },
    ],
  },
  {
    heading: 'TOKENS & MODULES',
    items: [
      { label: 'Module Management', iconName: 'streamline:module-three', path: '/module-management' },
    ],
  },
  {
    heading: 'OPERATIONS',
    items: [
      { label: 'Transactions', iconName: 'streamline:interface-time-clock-circle-clock-loading-measure-time-circle', path: '/transaction-history' },
      { label: 'Audit Reports', iconName: 'streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart', path: '/audit' },
    ],
  },
];

function isActive(pathname: string, itemPath: string): boolean {
  if (itemPath === '/dashboard') return pathname === '/dashboard' || pathname === '/';
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

// ─── Sidebar Content (shared between desktop and mobile) ─────────────────────

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
      {NAV_SECTIONS.map((section, sIdx) => (
        <div key={sIdx} className={sIdx > 0 ? 'pt-4' : ''}>
          {section.heading && (
            <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {section.heading}
            </div>
          )}
          {section.items.map((item) => {
            const active = isActive(location.pathname, item.path);
            return (
              <Link
                key={item.path + item.label}
                to={item.path}
                onClick={onNavigate}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon name={item.iconName} className="h-4 w-4 flex-shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

// ─── Layout Component ────────────────────────────────────────────────────────

export function AdminLayout() {
  const navigate = useNavigate();
  const { user, wallet, isAuthenticated, logout, connectWallet, disconnectWallet } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  async function handleConnectWallet() {
    setWalletLoading(true);
    try {
      await connectWallet();
    } finally {
      setWalletLoading(false);
    }
  }

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  const walletSection = (
    <div className="border-t border-border px-3 py-4 space-y-3">
      {/* Wallet */}
      {wallet ? (
        <button
          onClick={disconnectWallet}
          className="flex w-full items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/5 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-500/10 transition-colors"
        >
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4 flex-shrink-0" />
          <span className="font-mono text-xs truncate">{truncateAddress(wallet.address)}</span>
          <span className="ml-auto flex items-center gap-1 text-[11px] opacity-70">
            <Icon name="streamline:travel-map-earth-2-planet-earth-globe-world" className="h-3 w-3" />
            {wallet.network}
          </span>
        </button>
      ) : (
        <button
          onClick={handleConnectWallet}
          disabled={walletLoading}
          className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-60"
        >
          {walletLoading ? (
            <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 animate-spin" />
          ) : (
            <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
          )}
          {walletLoading ? 'Connecting...' : 'Connect Wallet'}
        </button>
      )}

      {/* User info + sign out */}
      <div className="flex items-center gap-2.5 px-1">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium flex-shrink-0">
          {user?.initials ?? 'AD'}
        </div>
        <div className="flex-1 min-w-0 text-xs">
          <div className="font-medium truncate">{user?.username ?? 'admin-1'}</div>
          <div className="text-muted-foreground truncate">{user?.role ?? 'Administrator'}</div>
        </div>
        <button
          onClick={handleLogout}
          className="rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          title="Sign out"
        >
          <Icon name="streamline:logout-1" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* ─── Desktop Sidebar ──────────────────────────────────────────────── */}
      <aside className="hidden md:flex md:w-60 flex-col border-r border-border bg-background sticky top-0 h-screen">
        {/* Logo */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            <AscendHorizonLogo size={24} className="text-primary" />
            <span className="font-mono text-xs text-muted-foreground">{'{ Admin }'}</span>
          </Link>
        </div>

        <SidebarNav />

        {walletSection}
      </aside>

      {/* ─── Mobile Sidebar Overlay ───────────────────────────────────────── */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Sidebar panel */}
          <aside className="fixed inset-y-0 left-0 z-50 w-72 flex flex-col border-r border-border bg-background md:hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <AscendHorizonLogo size={24} className="text-primary" />
                <span className="font-mono text-xs text-muted-foreground">{'{ Admin }'}</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-5 w-5" />
              </button>
            </div>

            <SidebarNav onNavigate={() => setMobileMenuOpen(false)} />

            {walletSection}
          </aside>
        </>
      )}

      {/* ─── Main Content ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Slim header (mobile: hamburger + logo; desktop: just badge + notifications) */}
        <header className="sticky top-0 z-30 border-b border-border bg-background">
          <div className="flex h-14 items-center justify-between px-4 sm:px-6">
            {/* Mobile: hamburger + logo */}
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="rounded-md p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="streamline:interface-setting-menu-1-button-parallel-horizontal-lines-menu-navigation-three-hamburger" className="h-5 w-5" />
              </button>
              <Link to="/dashboard" className="flex items-center gap-2">
                <AscendHorizonLogo size={22} className="text-primary" />
              </Link>
            </div>

            {/* Desktop: badge */}
            <div className="hidden md:block">
              <span className="rounded-full border border-primary/30 bg-primary/5 px-3 py-0.5 text-xs font-medium text-primary">
                For Internal Use Only
              </span>
            </div>

            {/* Right side: notifications */}
            <div className="flex items-center gap-2">
              <button className="relative rounded-md p-2 text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="streamline:interface-alert-alarm-bell-2-alert-bell-ring-notification-alarm" className="h-4 w-4" />
              </button>

              {/* Mobile: compact wallet indicator */}
              {wallet && (
                <button
                  onClick={disconnectWallet}
                  className="md:hidden flex items-center gap-1.5 rounded-lg border border-green-500/30 bg-green-500/5 px-2 py-1.5 text-xs font-medium text-green-600"
                >
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-3.5 w-3.5" />
                  <Icon name="streamline:electric-cord-3" className="h-3 w-3 opacity-60" />
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
